// CustomCursorCanvas — the one place that actually paints a 24×24 custom
// cursor onto a `<canvas>` element (custom-cursor-2026-09-19.md §2.5).
// Reused by:
//   - `BrushCursorOverlay` (own cursor, bottom-right of the brush ring)
//   - `NameLabelOverlay` (peer cursor, left of the name)
//   - `MemberPanel` / `ModernSocialDock` (participant list thumbnail)
//   - `CursorEditorPanel` (1x/2x live preview)
//
// HiDPI (§2.5 "HiDPI では 2x で描く"): the canvas **backing store** is drawn
// at `sizePx * BACKING_SCALE` device px while the CSS box stays `sizePx`,
// so dots stay crisp instead of being upscaled/blurred by the browser.
// `image-rendering: pixelated` additionally guards against any further
// browser-side scaling (e.g. a caller setting a CSS size that doesn't
// divide evenly) blurring the blocks.
//
// Drawing itself is delegated to the pure `drawCustomCursor` (fillRect-block
// idiom, see that file's header for why `ImageData`/`putImageData` is
// avoided in this package).

import { CURSOR_SIZE } from '@oekaki/protocol';
import { useEffect, useRef } from 'react';
import { drawCustomCursor } from './customCursorDraw.js';
import { createEmptyCursorPixels, isCursorPixelsBlank } from './customCursorPixels.js';

/** Fixed HiDPI backing-store multiplier (§2.5 — not devicePixelRatio-based, kept deterministic). */
export const CUSTOM_CURSOR_CANVAS_BACKING_SCALE = 2;

export interface CustomCursorCanvasProps {
  /** `null` = nothing to draw (component renders nothing unless `alwaysRender`). */
  readonly pixels: Uint8ClampedArray | null;
  /** CSS display size (square), px. */
  readonly sizePx: number;
  /** Paint a transparency checkerboard behind the pixels (editor / preview use). */
  readonly checker?: boolean;
  /** Draw 1px cell gridlines (editor grid use only). */
  readonly grid?: boolean;
  /**
   * Render (and keep an empty/blank canvas mounted) even when `pixels` is
   * `null` or fully transparent. The editor grid always wants a visible
   * surface to paint into; thumbnails (overlay/list) want to render nothing
   * at all when there's no cursor to show.
   */
  readonly alwaysRender?: boolean;
  readonly className?: string;
  /** When set, exposes the canvas as `role="img"` with this label; otherwise `aria-hidden`. */
  readonly title?: string;
}

export const CustomCursorCanvas = ({
  pixels,
  sizePx,
  checker,
  grid,
  alwaysRender = false,
  className,
  title,
}: CustomCursorCanvasProps): React.ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const shouldRender = alwaysRender || (pixels !== null && !isCursorPixelsBlank(pixels));
  const backingSizePx = Math.max(1, Math.round(sizePx * CUSTOM_CURSOR_CANVAS_BACKING_SCALE));

  useEffect(() => {
    if (!shouldRender) return;
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    // jsdom (component tests) has no real Canvas2D backend → null. Real
    // browsers always return a context here (same guard as ArgbSliderPanel.tsx).
    if (ctx === null) return;
    drawCustomCursor(ctx, pixels ?? createEmptyCursorPixels(), {
      cellPx: backingSizePx / CURSOR_SIZE,
      checker,
      grid,
    });
  }, [pixels, shouldRender, backingSizePx, checker, grid]);

  if (!shouldRender) return null;

  return (
    <canvas
      ref={canvasRef}
      data-oekaki-custom-cursor-canvas
      width={backingSizePx}
      height={backingSizePx}
      aria-hidden={title === undefined ? true : undefined}
      role={title !== undefined ? 'img' : undefined}
      aria-label={title}
      className={className}
      style={{ width: sizePx, height: sizePx, imageRendering: 'pixelated' }}
    />
  );
};
